import type { PortableTextBlock } from '@portabletext/react';
import { captureException } from '@sentry/react';
import { useInternetFlowStore, type InternetCustomerAddress, type SingleInternetCustomerAddress } from './useInternetStore';
import { trackAddressAvailabilityResult } from '~/analytics/eventTracker';
import { captureUXEventOnce } from '~/analytics/productAnalytics';

const displayDataQuery = encodeURIComponent(`*[_id=='isp-sales-page'][0] { ..., "image": image.asset->url }`);
export const displayDataPromise: Promise<{
  title: string;
  description: PortableTextBlock;
  image: string;
  legalTerms: string;
  tvOnlyTitle: string;
  tvOnlyDescription: string;
}> = fetch(`https://bj7ifs6s.apicdn.sanity.io/v2025-06-10/data/query/uat?query=${displayDataQuery}`)
  .then((res) => res.json())
  .then((data) => data.result);

export type SearchResponse = {
  installationAddresses: InternetCustomerAddress[];
};

const cache = new Map<string, Promise<SearchResponse>>();
export const ispAddressLookup = async (searchString: string): Promise<SearchResponse> => {
  captureUXEventOnce('rtv_perform_isp_address_lookup');
  if (!cache.has(searchString)) {
    cache.set(
      searchString,
      fetch(`/api/InternetServiceProviders/address/lookup?address=${searchString}`, {
        method: 'GET',
      })
        .then((r) => r.json())
        .then((json: SearchResponse) => mapResponseToAddresses(json)),
    );
  }
  try {
    return await (cache.get(searchString) as Promise<SearchResponse>);
  } catch (error) {
    captureException(new Error(`Failed to fetch ISP address lookup`, { cause: error }), { extra: { searchString } });
    return { installationAddresses: [] }; // Return empty array on error
  }
};

export const ispAddressRefine = async (addr: InternetCustomerAddress): Promise<SearchResponse> => {
  return await fetch(`/api/InternetServiceProviders/address/refine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addr),
  })
    .then((r) => r.json())
    .then((json: SearchResponse) => mapResponseToAddresses(json));
};

export type AvailabilityResponse = ReturnType<typeof mapAvailabilityResponse> | null;
const availabilityCache = new Map<string, Promise<AvailabilityResponse>>();
export const ispServiceAvailabilityCheck = async (addr: SingleInternetCustomerAddress) => {
  const cachedValue = availabilityCache.get(addr.description);
  if (cachedValue) {
    return await cachedValue;
  }
  const promise = fetch(`/api/InternetServiceProviders/address/availability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addr),
  })
    .then((r) => r.json())
    .then((json: NgtAvailabilityResponse) => {
      // Filter out stuff we dont need
      if (json.serviceAvailable === false) {
        trackAddressAvailabilityResult({ city: addr.city, postal_code: addr.postalCode, ngt_available: false });
        return [];
      }
      // store valid availability response in store
      useInternetFlowStore.setState({ validAvailabilityResponse: json });
      const mappedProducts = mapAvailabilityResponse([json]);
      trackAddressAvailabilityResult({ city: addr.city, postal_code: addr.postalCode, ngt_available: mappedProducts.length > 0 });
      return mappedProducts;
    });
  availabilityCache.set(addr.description, promise);
  return await promise;
};

const mapAvailabilityResponse = (data: NgtAvailabilityResponseYes[]) =>
  data
    .filter((item) => item.availabilityInfo.physicalMedia === 'FIBER')
    .map(({ availabilityInfo: availability, availablePackages: packages }) => ({
      availability: {
        physicalMedia: availability.physicalMedia,
        maxDownstreamBandwidth: availability.maxDownstreamBandwidth,
        maxUpstreamBandwidth: availability.maxUpstreamBandwidth,
        accessProvider: availability.accessProvider,
        lineOwner: availability.lineOwner,
      },
      packages: packages.map((p) => ({
        id: p.id,
        // picker_folder: p.picker_folder,
        templates: p.templates
          .filter((t) => t.category_name === 'Broadband')
          .map((t) => ({
            id: t.id,
            category_name: t.category_name,
            product_bandwidth_up_c: t.product_bandwidth_up_c,
            product_bandwidth_down_c: t.product_bandwidth_down_c,
            invoice_group_no_c: t.invoice_group_no_c,
            external_name_no_c: t.external_name_no_c,
            variations: t.variation.map((v) => ({
              id: v.id,
              // price: Number.parseFloat(v.price),
              // discount_amount: Number.parseFloat(v.discount_amount),
              // discount_percentage: Number.parseFloat(v.discount_percentage),
              price_vat: Number.parseFloat(v.price_vat),
              // subtotal: Number.parseFloat(v.subtotal),
              // total: Number.parseFloat(v.total),
              commitment: v.commitment,
            })),
          })),
      })),
    }));

const mapResponseToAddresses = (response: SearchResponse): SearchResponse => {
  for (const addr of response.installationAddresses) {
    // Add space after comma
    addr.description = addr.description.replace(/,(\S)/, ', $1');
    // Helper prop for discriminated union
    (addr as any).addrType = addr.count > 1 ? 'multiple' : 'single';
  }
  return response;
};

type NgtAvailabilityResponse =
  | {
      serviceAvailable: false;
    }
  | NgtAvailabilityResponseYes;
export type NgtAvailabilityResponseYes = {
  serviceAvailable: true;
  availabilityInfo: {
    physicalMedia: 'FIBER' | 'WIRELESS';
    maxDownstreamBandwidth: 1000000;
    maxUpstreamBandwidth: 1000000;
    accessProvider: 'NATIONAL TELENOR';
    lineOwner: 'TELENOR';
    // loopConnectionType: 'BITSTREAM';
    // orderReadiness: 'READY';
    addresses: Array<{ address: object; installationEffort: string; deliveryStatus: string }>;
    /** THe next properties need to be mapped onto here before checkout from 👆 we have no idea on how to select a specific address... */
    installationEffort: string | undefined;
    deliveryStatus: string | undefined;
  };
  /** list of different internet products like "Fiber 50" and "Fiber 100" */
  availablePackages: Array<{
    id: string;
    picker_folder: 'Fixed Wireless' | 'NGT.no' | 'RiksTV';
    // available_from: '2020-09-13';
    // available_to: '';
    // preference_group_c: 60;
    // cms_key_c: 'BITSTREAM_WIRELESS_TELIA_20MBIT';
    /** we only care about the "Broadband template" in here */
    templates: Array<{
      id: string;
      category_name: 'Broadband' | 'FILTER' | 'Setup-fee';
      product_bandwidth_up_c: 50000 | 100000 | 20000;
      product_bandwidth_down_c: 50000 | 100000 | 20000;
      invoice_group_no_c: 'Internett';
      external_name_no_c: 'Fiber 50' | 'Fiber 100' | 'Wireless Internet 20';
      // currencies: {
      //   id: '-99';
      // };
      // available_to_c: '';
      // sort_order_c: 2020;
      // billing_process_c: 'subscription';
      // available_from_c: '';
      // external_name_en_c: 'Wireless Internet 20';
      // oss_type_c: 'Internet Standard';
      // invoice_group_en_c: 'Internet';
      /** Variation for broadband includes monthly or yearly subscription variations of the product */
      variation: Array<{
        id: string;
        // price: `${number}`; // '479.200000';
        // discount_amount: `${number}`; // '0.000000';
        // discount_percentage: null;
        price_vat: `${number}`; // '599.000000';
        // subtotal: `${number}`; // '479.200000';
        // total: `${number}`; // '599.000000';
        commitment: '12M' | '';
        // vat_rate: 25;
        // start_offset: '0M';
        // end_offset: '12M' | '';
        // available_from: '';
        // available_to: '';
        // pricevariation_name: 'RiksTV.no Priser 2025';
      }>;
    }>;
  }>;
};
